import React, { useRef, useState } from 'react';
import debounce from 'debounce';

import Grid from '@material-ui/core/Grid';
import Radio from '@material-ui/core/Radio';
import RadioGroup from '@material-ui/core/RadioGroup';
import FormControlLabel from '@material-ui/core/FormControlLabel';
import FormControl from '@material-ui/core/FormControl';
import FormLabel from '@material-ui/core/FormLabel';
import InputAdornment from '@material-ui/core/InputAdornment';
import Paper from '@material-ui/core/Paper';
import TextField from '@material-ui/core/TextField';
import Typography from '@material-ui/core/Typography';

import { roundTo } from '../../billing/timesheets/BillingTools';
import DateInput from '../../../app/common/ui/DateInput';
import { any } from '../../../app/data/validate';

const AccommodationExpenses = ({ tariff, quote, timesheet, onChange }) => {
    if (!quote && !timesheet) throw new Error("quote or timesheet is required");
    if (!tariff) throw new Error("tariff is required");

    const items = quote || timesheet;
    const accomodation = items.get('ACCOM') || { key: 'ACCOM', category: 'expenses', section: 'accommodation', code: 'm25', rate: 0, quantity: 0, amount: 0 };

    const [location, setLocation] = useState(accomodation.code);
    const [rate, setRate] = useState(roundTo(accomodation.amount, 2));

    const costRef = useRef(accomodation.rate);
    const quantityRef = useRef(accomodation.quantity);
    const noteRef = useRef(accomodation.note || '');
    const [accomodationDate, setAccomodationDate] = useState(accomodation.date);

    const service = tariff.expenses.accommodation;

    const calculateRate = (totalCost, numberOfNights) => {
        const averageRate = (!numberOfNights || !totalCost) ? 0 : totalCost / numberOfNights;
        return roundTo(averageRate, 2);
    }

    const applyChange = ({ totalCost = 0, numberOfNights = 0, code = location, date }) => {
        const averageRate = calculateRate(totalCost, numberOfNights);

        const item = {
            key: service[location].key,
            category: 'expenses',
            section: 'accommodation',
            code,
            rate: averageRate,
            quantity: numberOfNights,
            note: noteRef.current.value
        };

        if (date) item.date = date;

        items.apply(item);

        setRate(averageRate);
        onChange();
    }

    const handleLocationChange = ({ target: { value } }) => {
        setLocation(value);
        applyChange({ totalCost: costRef.current.value, numberOfNights: quantityRef.current.value, code: value, date: accomodationDate });
    }

    const handleChange = debounce(() => {
        applyChange({ totalCost: costRef.current.value, numberOfNights: quantityRef.current.value, code: location, date: accomodationDate });
    }, 500);

    const handleDateChange = date => {
        setAccomodationDate(date);
        applyChange({ totalCost: costRef.current.value, numberOfNights: quantityRef.current.value, code: location, date });
    }

    return (
        <Paper style={{ padding: '0rem 1rem 1rem 1rem', marginTop: '1rem' }}>
            <Grid container spacing={2} >
                <Grid item xs={8}>
                    <Typography variant="h6">Accommodation Expenses</Typography>
                </Grid>
            </Grid>
            <FormControl component="fieldset">
                <FormLabel component="legend">Location</FormLabel>
                <RadioGroup row aria-label="hotel location" name="location" value={location} onChange={handleLocationChange}>
                    <FormControlLabel value="m25" control={<Radio />} label={service.m25.description} />
                    <FormControlLabel value="birmingham" control={<Radio />} label={service.birmingham.description} />
                    <FormControlLabel value="liverpool" control={<Radio />} label={service.liverpool.description} />
                    <FormControlLabel value="leeds" control={<Radio />} label={service.leeds.description} />
                    <FormControlLabel value="manchester" control={<Radio />} label={service.manchester.description} />
                    <FormControlLabel value="newcastle" control={<Radio />} label={service.newcastle.description} />
                    <FormControlLabel value="edinburgh" control={<Radio />} label={service.edinburgh.description} />
                    <FormControlLabel value="glasgow" control={<Radio />} label={service.glasgow.description} />
                    <FormControlLabel value="other" control={<Radio />} label={service.other.description} />
                </RadioGroup>
            </FormControl>
            <Grid container item direction="row" justify="flex-start" alignItems="center" spacing={2}>
                <Grid item xs={2}>
                    <div>
                        <Typography variant="subtitle2">Number of Nights</Typography>
                    </div>
                    <TextField
                        inputRef={quantityRef}
                        defaultValue={accomodation.quantity}
                        style={{ minWidth: '100%' }}
                        type="number"
                        helperText="Please keep receipts"
                        inputProps={{
                            min: 0
                        }}
                        onChange={handleChange} />
                </Grid>
                {
                    timesheet &&
                    <Grid item xs={2}>
                        <div>
                            <Typography variant="subtitle2">Date</Typography>
                        </div>
                        <DateInput value={accomodationDate} required={any(costRef.current.value, quantityRef.current.value)} onChange={handleDateChange} hint="Date (or first date)" />
                    </Grid>

                }
                <Grid item xs={2}>
                    <div>
                        <Typography variant="subtitle2">Total Cost</Typography>
                    </div>
                    <TextField
                        inputRef={costRef}
                        defaultValue={accomodation.amount}
                        style={{ minWidth: '100%' }}
                        type="number"
                        helperText="Including breakfast"
                        onChange={handleChange}
                        inputProps={{
                            min: 0
                        }}
                        InputProps={{
                            startAdornment: <InputAdornment position="start">£</InputAdornment>,
                        }}
                    />
                </Grid>
                {
                    quote &&
                    <Grid item xs={2}>
                        <div>
                            <Typography variant="subtitle2">Average Rate per Night</Typography>
                        </div>
                        <TextField
                            style={{ minWidth: '100%' }}
                            disabled
                            type="number"
                            value={rate}
                            helperText={service[location].hint}
                        />
                    </Grid>
                }                <Grid item xs={6}>
                    <div>
                        <Typography variant="subtitle2">Notes</Typography>
                    </div>
                    <TextField
                        inputRef={noteRef}
                        defaultValue={accomodation.note || ''}
                        onChange={handleChange}
                        style={{ minWidth: '100%' }}
                        helperText="Optional"
                    />
                </Grid>
            </Grid>
        </Paper>
    );
};

export default AccommodationExpenses;